// src/pages/admin/AdminForgotPassword.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';

const API_URL = 'https://medigo-api.onrender.com/api';

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); 
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState(''); 
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setSuccess('Verification code sent to your email! Check your inbox.');
        setStep(2);
        startResendCooldown();
        
     
        if (response.data.otp && !response.data.emailSent) {
          
        }
      } else {
        setError(response.data.message || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('Request OTP error:', err);
      setError(err.response?.data?.message || 'Failed to send verification code. Please check your email and try again.');
    } finally {
      setLoading(false);
    }
  };


  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/auth/verify-otp`, {
        email: email.toLowerCase().trim(),
        otp: otpCode,
        type: 'password_reset' 
      });

      if (response.data.success) {
        
        if (response.data.resetToken) {
          setResetToken(response.data.resetToken);
          setSuccess('Code verified successfully! Now set your new password.');
          setStep(3);
        } else {
          setError('Verification successful but reset token missing. Please try again.');
        }
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.response?.data?.message || 'Invalid or expired verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!resetToken) {
      setError('Reset token missing. Please verify your code again.');
      setStep(2);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.toLowerCase().trim(),
        resetToken: resetToken, 
        newPassword
      });

      if (response.data.success) {
        setSuccess('Password reset successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  
  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; 

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  
  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  
  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    
    
    const lastIndex = Math.min(pastedData.length, 5);
    const lastInput = document.getElementById(`otp-${lastIndex}`);
    if (lastInput) lastInput.focus();
  };

  
  const startResendCooldown = () => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setSuccess('New verification code sent to your email!');
        setOtp(['', '', '', '', '', '']);
        startResendCooldown();
      }
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Admin Portal Password Recovery
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center space-x-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
            {step > 1 ? '✓' : '1'}
          </div>
          <div className={`w-16 h-1 ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
            {step > 2 ? '✓' : '2'}
          </div>
          <div className={`w-16 h-1 ${step >= 3 ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
            3
          </div>
        </div>

        <div className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-md">
          {error && <Alert type="error" message={error} onClose={() => setError('')} />}
          {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Enter your email address</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We'll send you a 6-digit verification code to reset your password.
                </p>
                <Input
                  label="Admin Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@medigo.co.ke"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>

              <Button type="submit" variant="primary" fullWidth loading={loading} disabled={loading}>
                {loading ? 'Sending...' : 'Send Verification Code'}
              </Button>

              <div className="text-center text-sm">
                <Link to="/admin/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                  ← Back to Login
                </Link>
              </div>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Enter verification code</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We sent a 6-digit code to <strong>{email}</strong>
                </p>

                <div className="flex justify-center gap-2 mb-4">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(index, e)}
                      onPaste={index === 0 ? handleOTPPaste : undefined}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                      disabled={loading}
                    />
                  ))}
                </div>

                <div className="text-center">
                  {resendCooldown > 0 ? (
                    <p className="text-sm text-gray-600">
                      Resend code in <span className="font-semibold text-indigo-600">{resendCooldown}s</span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                      disabled={loading}
                    >
                      Didn't receive code? Resend
                    </button>
                  )}
                </div>
              </div>

              <Button type="submit" variant="primary" fullWidth loading={loading} disabled={loading || otp.join('').length !== 6}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>

              <div className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                  disabled={loading}
                >
                  ← Change Email
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Set New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Create new password</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Your password must be at least 6 characters long.
                </p>

                <Input
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  autoFocus
                  disabled={loading}
                />

                <div className="mt-4">
                  <Input
                    label="Confirm Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                    disabled={loading}
                  />
                </div>

                {/* Password strength indicator */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 text-xs">
                      <div className={`h-1 w-1/3 rounded ${newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-1 w-1/3 rounded ${newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-1 w-1/3 rounded ${newPassword.length >= 10 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {newPassword.length < 6 ? 'Weak' : newPassword.length < 8 ? 'Medium' : 'Strong'}
                    </p>
                  </div>
                )}
              </div>

              <Button type="submit" variant="primary" fullWidth loading={loading} disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>

        <div className="text-center text-xs text-gray-500">
          <p>© 2025 MediGo. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPassword;